import {
  normalizePropertyAddress,
  samePropertyAddress,
  type NormalizedPropertyAddress,
  type PropertyAddressInput
} from "./property-value.js";

/** Stable tool identity for ATTOM AVM lookups. Separate from Zestimate forever. */
export const ATTOM_AVM_LOOKUP_VERSION = "attom-avm-v1";
export const ATTOM_AVM_INPUT_VERSION = "attom-avm-input-v1";
export const ATTOM_AVM_POLICY_VERSION = "attom-avm-policy-v1";

/**
 * Labels the ATTOM adapter in evidence only. Cache identity is the normalized
 * address plus the ATTOM tool id — never the Zestimate cache key.
 */
export const ATTOM_AVM_PROVIDER_VERSION = "attom-propertyapi-avm-detail-v1";

export const ATTOM_AVM_VALUE_SOURCE = "ATTOM AVM";

export const ATTOM_AVM_FAILURE_CLASSES = [
  "attom_avm_input_invalid",
  "attom_avm_not_configured",
  "attom_avm_auth_failed",
  "attom_avm_rate_limited",
  "attom_avm_no_match",
  "attom_avm_ambiguous_match",
  "attom_avm_address_mismatch",
  "attom_avm_unavailable",
  "attom_avm_timeout",
  "attom_avm_invalid_response",
  "attom_avm_provider_failure",
  "attom_avm_unexpected"
] as const;

export type AttomAvmFailureClass = (typeof ATTOM_AVM_FAILURE_CLASSES)[number];

export const ATTOM_AVM_RETRYABLE_FAILURE_CLASSES = new Set<AttomAvmFailureClass>([
  "attom_avm_rate_limited",
  "attom_avm_timeout",
  "attom_avm_provider_failure"
]);

export type AttomAvmObservation = {
  requestedAddress: NormalizedPropertyAddress;
  matchedAddress: NormalizedPropertyAddress;
  attomId: string | null;
  marketValue: number;
  low: number | null;
  high: number | null;
  confidence: number | null;
  asOf: string | null;
  observedAt: string;
  providerVersion: string;
  source: typeof ATTOM_AVM_VALUE_SOURCE;
  /** Provider-permitted metadata only — never the API key. */
  sourceMetadata: Record<string, unknown>;
};

export type AttomAvmMappedSuccess = {
  ok: true;
  observation: AttomAvmObservation;
};

export type AttomAvmMappedFailure = {
  ok: false;
  failureClass: AttomAvmFailureClass;
  message: string;
  retryable: boolean;
  requestedAddress: NormalizedPropertyAddress | null;
  matchedAddress: NormalizedPropertyAddress | null;
  attomId: string | null;
  details?: Record<string, unknown>;
};

export type AttomAvmMappedResult = AttomAvmMappedSuccess | AttomAvmMappedFailure;

export type AttomAvmToolInput = {
  workItemId: string;
  address: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
  };
};

const SECRET_KEY_PATTERN = /api[_-]?key|authorization|password|secret|token|credential/i;
const SECRET_VALUE_PATTERN = /\b(?:apikey|api_key|bearer)\b\s*[:=]\s*["']?([A-Za-z0-9._\-]{8,})/gi;

/** True when a failure class may receive capped transport backoff. */
export function isAttomAvmRetryableFailure(failureClass: string | null | undefined): boolean {
  return failureClass != null && ATTOM_AVM_RETRYABLE_FAILURE_CLASSES.has(failureClass as AttomAvmFailureClass);
}

/**
 * Redacts credential-bearing keys and common secret-shaped values from any
 * object that might later appear in logs, Sheets payloads, or artifacts.
 */
export function redactAttomSecrets<T>(value: T): T {
  return redactValue(value) as T;
}

function redactValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(redactValue);
  if (!value || typeof value !== "object") {
    if (typeof value === "string") {
      return value.replace(SECRET_VALUE_PATTERN, (match) => match.replace(/([A-Za-z0-9._\-]{8,})/g, "[REDACTED]"));
    }
    return value;
  }
  const out: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    out[key] = SECRET_KEY_PATTERN.test(key) ? "[REDACTED]" : redactValue(child);
  }
  return out;
}

/**
 * Maps a raw ATTOM AVM HTTP outcome into a typed publishable observation or a
 * typed failure. The backend still validates address identity before any
 * numeric value is published.
 */
export function mapAttomAvmHttpResult(input: {
  requested: PropertyAddressInput | NormalizedPropertyAddress;
  status: number;
  body: unknown;
  observedAt?: string;
  providerVersion?: string;
  timedOut?: boolean;
  transportError?: string | null;
}): AttomAvmMappedResult {
  const requestedAddress = isNormalizedAddress(input.requested)
    ? input.requested
    : normalizePropertyAddress(input.requested);
  if (!requestedAddress) {
    return failure("attom_avm_input_invalid", "Property address is invalid.", false, null, null, null);
  }

  if (input.timedOut) {
    return failure(
      "attom_avm_timeout",
      "ATTOM AVM request timed out.",
      true,
      requestedAddress,
      null,
      null
    );
  }

  if (input.transportError) {
    return failure(
      "attom_avm_provider_failure",
      "ATTOM AVM transport failed.",
      true,
      requestedAddress,
      null,
      null,
      { transportError: input.transportError }
    );
  }

  const status = input.status;
  if (status === 401 || status === 403) {
    return failure(
      "attom_avm_auth_failed",
      "ATTOM rejected the configured credential.",
      false,
      requestedAddress,
      null,
      null,
      { httpStatus: status }
    );
  }
  if (status === 429) {
    return failure(
      "attom_avm_rate_limited",
      "ATTOM rate-limited the AVM request.",
      true,
      requestedAddress,
      null,
      null,
      { httpStatus: status }
    );
  }
  if (status === 400) {
    // ATTOM often uses 400 for unmatched address searches.
    const bodyRecord = asRecord(input.body);
    const msg = String(bodyRecord?.status && asRecord(bodyRecord.status)?.msg || bodyRecord?.message || "");
    if (/no.?result|not found|no property|successwithoutresult/i.test(msg) || bodySaysNoResult(input.body)) {
      return failure(
        "attom_avm_no_match",
        "ATTOM returned no matching property for the normalized address.",
        false,
        requestedAddress,
        null,
        null,
        { httpStatus: status }
      );
    }
    return failure(
      "attom_avm_invalid_response",
      "ATTOM rejected the AVM request.",
      false,
      requestedAddress,
      null,
      null,
      { httpStatus: status }
    );
  }
  if (status >= 500 || status === 0) {
    return failure(
      "attom_avm_provider_failure",
      "ATTOM AVM provider failure.",
      true,
      requestedAddress,
      null,
      null,
      { httpStatus: status }
    );
  }
  if (status < 200 || status >= 300) {
    return failure(
      "attom_avm_provider_failure",
      `ATTOM AVM returned HTTP ${status}.`,
      status >= 500,
      requestedAddress,
      null,
      null,
      { httpStatus: status }
    );
  }

  const body = asRecord(input.body);
  if (!body) {
    return failure(
      "attom_avm_invalid_response",
      "ATTOM AVM response was not a JSON object.",
      false,
      requestedAddress,
      null,
      null
    );
  }

  const statusBlock = asRecord(body.status);
  const statusMsg = String(statusBlock?.msg ?? statusBlock?.message ?? "");
  const statusCode = Number(statusBlock?.code);
  if (bodySaysNoResult(body) || /successwithoutresult|no.?result|not found/i.test(statusMsg) || statusCode === 1) {
    return failure(
      "attom_avm_no_match",
      "ATTOM returned no matching property for the normalized address.",
      false,
      requestedAddress,
      null,
      null,
      { providerStatus: redactAttomSecrets(statusBlock ?? {}) }
    );
  }

  const properties = Array.isArray(body.property)
    ? body.property
    : body.property
      ? [body.property]
      : [];
  if (properties.length === 0) {
    return failure(
      "attom_avm_no_match",
      "ATTOM returned no matching property for the normalized address.",
      false,
      requestedAddress,
      null,
      null
    );
  }
  if (properties.length > 1) {
    return failure(
      "attom_avm_ambiguous_match",
      "ATTOM returned multiple property matches; human review is required.",
      false,
      requestedAddress,
      null,
      null,
      { matchCount: properties.length }
    );
  }

  const property = asRecord(properties[0]);
  if (!property) {
    return failure(
      "attom_avm_invalid_response",
      "ATTOM property payload was malformed.",
      false,
      requestedAddress,
      null,
      null
    );
  }

  const matchedAddress = normalizeAttomPropertyAddress(property, requestedAddress);
  const attomId = extractAttomId(property);
  if (!matchedAddress) {
    return failure(
      "attom_avm_invalid_response",
      "ATTOM property address could not be normalized.",
      false,
      requestedAddress,
      null,
      attomId
    );
  }
  if (!samePropertyAddress(requestedAddress, matchedAddress)) {
    return failure(
      "attom_avm_address_mismatch",
      "The ATTOM property did not match the requested normalized address.",
      false,
      requestedAddress,
      matchedAddress,
      attomId
    );
  }

  const avm = asRecord(property.avm) ?? asRecord(property.AVM);
  if (!avm) {
    return failure(
      "attom_avm_unavailable",
      "The matched ATTOM property did not expose an AVM.",
      false,
      requestedAddress,
      matchedAddress,
      attomId
    );
  }

  const amount = asRecord(avm.amount) ?? asRecord(avm.Amount) ?? avm;
  const marketValue = finitePositiveNumber(
    amount?.value ?? amount?.Value ?? avm.value ?? avm.estimatedValue ?? avm.amount
  );
  if (marketValue === null) {
    return failure(
      "attom_avm_unavailable",
      "The matched ATTOM property did not expose a usable AVM point estimate.",
      false,
      requestedAddress,
      matchedAddress,
      attomId,
      { avm: redactAttomSecrets(avm) }
    );
  }

  const low = finitePositiveNumber(amount?.low ?? amount?.Low ?? avm.low ?? avm.min);
  const high = finitePositiveNumber(amount?.high ?? amount?.High ?? avm.high ?? avm.max);
  const confidence = finiteNumber(avm.confidence ?? avm.confidenceScore ?? avm.FSD ?? amount?.scr);
  const asOf = stringOrNull(avm.eventDate ?? avm.asOf ?? avm.valuationDate ?? avm.date)
    ?? stringOrNull(asRecord(property.vintage)?.lastModified);

  const observedAt = input.observedAt && !Number.isNaN(Date.parse(input.observedAt))
    ? input.observedAt
    : new Date().toISOString();

  return {
    ok: true,
    observation: {
      requestedAddress,
      matchedAddress,
      attomId,
      marketValue,
      low,
      high,
      confidence,
      asOf,
      observedAt,
      providerVersion: input.providerVersion ?? ATTOM_AVM_PROVIDER_VERSION,
      source: ATTOM_AVM_VALUE_SOURCE,
      sourceMetadata: redactAttomSecrets({
        provider: "attom",
        endpoint: "propertyapi/v1.0.0/avm/detail",
        statusCode: Number.isFinite(statusCode) ? statusCode : null,
        statusMsg: statusMsg || null
      })
    }
  };
}

/** Builds ATTOM address1/address2 query parts from a normalized address. */
export function attomAvmAddressQuery(address: NormalizedPropertyAddress): { address1: string; address2: string } {
  const address1 = [address.street, address.unit].filter(Boolean).join(" ");
  const address2 = `${address.city}, ${address.state} ${address.postalCode}`;
  return { address1, address2 };
}

export function attomAvmCacheKey(addressKey: string): string {
  return ["attom_avm_lookup", addressKey].join(":");
}

export function attomAvmOperatorMessage(failureClass: string): string {
  switch (failureClass) {
    case "attom_avm_input_invalid":
      return "Enter either a full address as Street, City, State Name or ST ZIP, or separate street, city, state, and ZIP fields.";
    case "attom_avm_not_configured":
      return "ATTOM AVM is not configured on the control plane.";
    case "attom_avm_auth_failed":
      return "ATTOM rejected the configured credential.";
    case "attom_avm_rate_limited":
      return "ATTOM rate-limited the AVM request.";
    case "attom_avm_no_match":
      return "ATTOM returned no matching property for the normalized address.";
    case "attom_avm_ambiguous_match":
      return "ATTOM returned multiple property matches; human review is required.";
    case "attom_avm_address_mismatch":
      return "The ATTOM property did not match the requested normalized address.";
    case "attom_avm_unavailable":
      return "The matched ATTOM property did not expose a usable AVM.";
    case "attom_avm_timeout":
      return "ATTOM AVM request timed out.";
    case "attom_avm_invalid_response":
      return "ATTOM returned an invalid AVM response.";
    case "attom_avm_provider_failure":
      return "ATTOM AVM provider failure.";
    default:
      return "Unexpected ATTOM AVM failure.";
  }
}

function normalizeAttomPropertyAddress(
  property: Record<string, unknown>,
  fallback: NormalizedPropertyAddress
): NormalizedPropertyAddress | null {
  const address = asRecord(property.address) ?? asRecord(property.Address) ?? {};
  const line1 = stringOrNull(address.line1 ?? address.address1 ?? address.oneLine)
    ?? stringOrNull(fallback.street);
  const locality = stringOrNull(address.locality ?? address.city) ?? fallback.city;
  const state = stringOrNull(address.countrySubd ?? address.state) ?? fallback.state;
  const postal = stringOrNull(address.postal1 ?? address.postalCode ?? address.zip) ?? fallback.postalCode;
  const unit = stringOrNull(address.unit ?? address.line2) ?? fallback.unit;

  // Prefer structured fields; fall back to oneLine only when structured parts fail.
  const structured = normalizePropertyAddress({
    street: line1,
    unit,
    city: locality,
    state,
    postalCode: postal
  });
  if (structured) return structured;

  const oneLine = stringOrNull(address.oneLine ?? address.line2);
  if (oneLine) {
    const parsed = normalizePropertyAddress({ fullAddress: oneLine });
    if (parsed) return parsed;
  }

  // Some ATTOM payloads omit city while still returning the same house/ZIP.
  // Reconstruct from the requested identity only when house number + ZIP agree.
  if (line1 && postal) {
    return normalizePropertyAddress({
      street: line1,
      unit: unit ?? undefined,
      city: fallback.city,
      state: fallback.state,
      postalCode: postal
    });
  }
  return null;
}

function extractAttomId(property: Record<string, unknown>): string | null {
  const identifier = asRecord(property.identifier) ?? asRecord(property.Identifier) ?? {};
  const raw = identifier.attomId ?? identifier.AttomId ?? identifier.Id ?? identifier.id ?? property.attomId;
  if (raw == null) return null;
  const text = String(raw).trim();
  return text && /^[A-Za-z0-9._:-]{1,120}$/.test(text) ? text : null;
}

function bodySaysNoResult(body: unknown): boolean {
  const record = asRecord(body);
  if (!record) return false;
  const status = asRecord(record.status);
  const total = Number(status?.total ?? record.total);
  if (Number.isFinite(total) && total === 0) return true;
  const msg = String(status?.msg ?? status?.message ?? "");
  return /successwithoutresult|no.?result|not found/i.test(msg);
}

function failure(
  failureClass: AttomAvmFailureClass,
  message: string,
  retryable: boolean,
  requestedAddress: NormalizedPropertyAddress | null,
  matchedAddress: NormalizedPropertyAddress | null,
  attomId: string | null,
  details?: Record<string, unknown>
): AttomAvmMappedFailure {
  return {
    ok: false,
    failureClass,
    message,
    retryable,
    requestedAddress,
    matchedAddress,
    attomId,
    ...(details ? { details: redactAttomSecrets(details) } : {})
  };
}

function isNormalizedAddress(value: unknown): value is NormalizedPropertyAddress {
  if (!value || typeof value !== "object") return false;
  const record = value as Record<string, unknown>;
  return typeof record.comparisonKey === "string"
    && typeof record.display === "string"
    && typeof record.street === "string"
    && typeof record.city === "string"
    && typeof record.state === "string"
    && typeof record.postalCode === "string";
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function stringOrNull(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value).replace(/\s+/g, " ").trim();
  return text || null;
}

function finitePositiveNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) return value;
  if (typeof value === "string") {
    const normalized = value.replace(/[$,\s]/g, "").trim();
    if (!normalized) return null;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }
  return null;
}

function finiteNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}
